import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { FolderKanban, Calendar, AlertCircle, FileText, ArrowRight, UserPlus, Bell, CheckCheck, X, Send, Clock } from 'lucide-react';

export default function ContractorDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({
    requested_for_name: '',
    requested_for_email: '',
    requested_role: '',
    project_id: '',
    reason: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
    loadNotifications();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.projects.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications?limit=20');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Use the link field if available (most notifications have this)
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.entity_type && notification.entity_id && notification.project_id) {
      // Fallback for older notification format
      const entityRoutes = {
        'site_instruction': `/projects/${notification.project_id}/si/${notification.entity_id}`,
        'rfi': `/projects/${notification.project_id}/rfi/${notification.entity_id}`,
        'delay_event': `/events/${notification.entity_id}`,
        'notice': `/notices/${notification.entity_id}`,
        'claim': `/claims/${notification.entity_id}`,
        'drawing_revision': `/projects/${notification.project_id}/drawings/${notification.entity_id}`,
        'correspondence': `/projects/${notification.project_id}?tab=correspondence`,
        'shop_drawing': `/shop-drawings/${notification.entity_id}`,
      };
      const route = entityRoutes[notification.entity_type];
      if (route) {
        navigate(route);
      } else if (notification.project_id) {
        navigate(`/projects/${notification.project_id}`);
      }
    } else if (notification.project_id) {
      navigate(`/projects/${notification.project_id}`);
    }
    setNotificationsOpen(false);
  };

  const handleRequestUser = async (e) => {
    e.preventDefault();
    try {
      await api.userRequests.create(requestForm);
      toast.success('User request submitted. Admin will review and approve.');
      setRequestDialogOpen(false);
      setRequestForm({
        requested_for_name: '',
        requested_for_email: '',
        requested_role: '',
        project_id: '',
        reason: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contractor-dashboard">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-barlow font-bold uppercase tracking-tight mb-1 sm:mb-2">My Projects</h1>
              <p className="text-sm sm:text-base text-slate-600">Manage your construction delay events and claims</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Notifications Bell */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="relative rounded-sm shrink-0"
                    data-testid="notifications-bell"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 p-0" align="end">
                  <div className="flex items-center justify-between p-3 border-b">
                    <h3 className="font-semibold text-sm">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={markAllAsRead}
                        className="text-xs h-7"
                      >
                        <CheckCheck className="h-3 w-3 mr-1" />
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No notifications
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <div 
                          key={notification.id}
                          className={`p-3 border-b hover:bg-slate-50 cursor-pointer ${!notification.read ? 'bg-blue-50/50' : ''}`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`mt-0.5 p-1.5 rounded shrink-0 ${
                              notification.type === 'site_instruction' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {notification.type === 'site_instruction' ? <Send className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {notification.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1 flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {new Date(notification.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 shrink-0"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-3" data-testid="request-user-btn">
                    <UserPlus className="h-4 w-4 sm:mr-2 shrink-0" />
                    <span className="hidden sm:inline">Request New User</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-xl font-barlow font-bold uppercase">Request New User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRequestUser} className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Name</Label>
                    <Input
                      value={requestForm.requested_for_name}
                      onChange={(e) => setRequestForm({ ...requestForm, requested_for_name: e.target.value })}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Email</Label>
                    <Input
                      type="email"
                      value={requestForm.requested_for_email}
                      onChange={(e) => setRequestForm({ ...requestForm, requested_for_email: e.target.value })}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Role</Label>
                    <Select value={requestForm.requested_role} onValueChange={(value) => setRequestForm({ ...requestForm, requested_role: value })}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="site_agent">Site Agent</SelectItem>
                        <SelectItem value="qs">Quantity Surveyor</SelectItem>
                        <SelectItem value="planner">Planner</SelectItem>
                        <SelectItem value="contracts_manager">Contracts Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Project (Optional)</Label>
                    <Select value={requestForm.project_id} onValueChange={(value) => setRequestForm({ ...requestForm, project_id: value })}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Reason</Label>
                    <Textarea
                      value={requestForm.reason}
                      onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                      required
                      rows={3}
                      placeholder="Why do you need this user?"
                      className="rounded-sm"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3">
                    Submit Request
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500 font-mono">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 sm:p-12 text-center">
            <FolderKanban className="h-12 w-12 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-lg sm:text-xl font-barlow font-bold uppercase mb-2">No Projects Yet</h3>
            <p className="text-sm sm:text-base text-slate-600">Contact your administrator to get assigned to projects</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <p className="col-span-full text-xs sm:text-sm text-slate-500 -mb-3">Select a project below to access module details, schedules, and reports.</p>
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-white border border-slate-200 rounded-sm shadow-sm hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <FolderKanban className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400 group-hover:text-slate-900 transition-colors" strokeWidth={1.5} />
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500 px-2 py-1 bg-slate-100 rounded">
                      {project.contract_type}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-xl font-barlow font-bold uppercase tracking-tight mb-2 group-hover:text-secondary transition-colors">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4 line-clamp-2">{project.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500 font-mono">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                    <ArrowRight className="h-4 w-4 text-secondary group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}